import { useCallback } from 'react';
import { notify } from '../helper/notify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from './useRedux';
import { transformDeductionToWorkingHour } from '../utils/deduction.utils';
import { addTimesheetWorkingHours, updateTimesheetWorkingHours } from '../features/timesheet/timesheet.slice';
import type { TimesheetWorkingHoursV2 } from '../features/timesheet/timesheet.service';

interface UseDeductionPageSubmitProps {
  editingDeductionData: any | null;
  selectedCustomerId: number | null;
  preferences: any;
  globalData: any;
  userId: number;
}

export const useDeductionPageSubmit = ({
  editingDeductionData,
  selectedCustomerId,
  preferences,
  globalData,
  userId
}: UseDeductionPageSubmitProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleDeductionSubmit = useCallback(
    async (deductionData: any): Promise<void> => {
      return new Promise(async (resolve, reject) => {
        try {
          // Validate customer selection
          if (!selectedCustomerId) {
            const error = new Error(String(t('CLIENT_REQUIRED')));
            notify.error(String(t('CLIENT_REQUIRED')));
            reject(error);
            return;
          }

          // Get current VAT and currency
          const currentVat = preferences?.vat || 25;
          const currency = preferences?.currency || 'SEK';
          const currencyRate = (
            globalData?.currencies?.find((item: any) => item.description === currency)
              ?.globalValue || '1'
          ).toString();

          // Transform deduction to workingHour format
          const workingHour = await transformDeductionToWorkingHour(
            deductionData,
            currentVat,
            currencyRate
          );

          if (!workingHour) {
            throw new Error('Failed to transform deduction data');
          }

          if (editingDeductionData) {
            // EDIT MODE: Update existing deduction via PUT /timesheetv2/update-timesheet-hours
            const itemId = editingDeductionData.timeSheetWorkingHourId || editingDeductionData.id || 0;

            const updatedHour: any = {
              id: itemId,
              timeSheetWorkingHourId: itemId,
              invoiceId: editingDeductionData.invoiceId ?? 0,
              workingDate: workingHour.workingDate || editingDeductionData.workingDate || '',
              houseworkType: workingHour.houseworkType || editingDeductionData.houseworkType || '',
              workingHour: workingHour.workingHour || 0,
              unit: workingHour.unit || editingDeductionData.unit || 'Tim',
              userId: editingDeductionData.userId || userId,
              customerId: selectedCustomerId,
              workingDescription: workingHour.workingDescription || editingDeductionData.workingDescription || '',
              customWorkingDescription: workingHour.workingDescription || editingDeductionData.workingDescription || '',
              amountPerUnit: workingHour.amountPerUnit || 0,
              amountPerUnitSek: workingHour.amountPerUnitSek || 0,
              foodAllowance: workingHour.foodAllowance || 0,
              number: workingHour.number || 0,
              articleNumber: workingHour.articleNumber || editingDeductionData.articleNumber || '',
              dateId: workingHour.dateId || editingDeductionData.dateId || '',
              isTaxFreeArticle: workingHour.isTaxFreeArticle ?? editingDeductionData.isTaxFreeArticle ?? false,
              isArticleShownInInvoice: workingHour.isArticleShownInInvoice ?? editingDeductionData.isArticleShownInInvoice ?? true,
              isPdfAttachedToInvoice: workingHour.isPdfAttachedToInvoice ?? editingDeductionData.isPdfAttachedToInvoice ?? true,
              tripStartedPlace: workingHour.tripStartedPlace || '',
              tripEndedPlace: workingHour.tripEndedPlace || '',
              distance: workingHour.distance || 0,
              country: workingHour.country || '',
              allowancePerDay: workingHour.allowancePerDay || 0,
              reasonForTrip: workingHour.reasonForTrip || '',
              carRegistrationNo: workingHour.carRegistrationNo || '',
              vat: workingHour.vat ?? 0,
              tripStartedDate: workingHour.tripStartedDate || '',
              tripEndedDate: workingHour.tripEndedDate || '',
              amount: workingHour.amount || 0,
              amountInSek: workingHour.amountInSek || workingHour.amount || 0,
              totalDayOfTravels: workingHour.totalDayOfTravels || 0,
              allowanceDestination: workingHour.allowanceDestination || '',
              description: editingDeductionData.description || '',
              expenseCategory: workingHour.expenseCategory || '',
              fileName: workingHour.fileName || '',
              fileContent: workingHour.fileContent || '',
            };

            const updatePayload = {
              customerId: selectedCustomerId!,
              timeSheetWorkingHoursV2: [updatedHour],
            };

            const result = await dispatch(updateTimesheetWorkingHours(updatePayload)).unwrap();

            if (result?.statusCode === 200 || result?.statusCode === 201) {
              notify.success(
                String(t('DEDUCTION_UPDATED_SUCCESSFULLY')) || 'Deduction updated successfully'
              );
              resolve();
            } else {
              throw new Error(result?.message || 'Failed to update deduction');
            }
          } else {
            // CREATE MODE: Add timesheet working hours with deduction
            
            // Convert WorkingHour to TimesheetWorkingHoursV2 format
            const timesheetWorkingHour: any = {
              id: 0,
              dateId: workingHour.dateId,
              workingHour: workingHour.workingHour || 0,
              houseworkType: workingHour.houseworkType || '',
              fileName: workingHour.fileName || '',
              fileContent: workingHour.fileContent || '',
              articleId: workingHour.articleId || 0,
              workingDate: workingHour.workingDate,
              number: workingHour.number,
              unit: workingHour.unit,
              vat: workingHour.vat,
              isArticleShownInInvoice: workingHour.isArticleShownInInvoice,
              amountPerUnit: workingHour.amountPerUnit,
              amountPerUnitSek: workingHour.amountPerUnitSek,
              amount: workingHour.amount,
              articleNumber: workingHour.articleNumber,
              workingDescription: workingHour.workingDescription,
              customWorkingDescription: '',
              userId: userId,
              customerId: selectedCustomerId,
              isTaxFreeArticle: workingHour.isTaxFreeArticle,
              isPdfAttachedToInvoice: workingHour.isPdfAttachedToInvoice,
              tripStartedPlace: workingHour.tripStartedPlace || '',
              tripEndedPlace: workingHour.tripEndedPlace || '',
              distance: workingHour.distance || 0,
              country: workingHour.country || '',
              allowancePerDay: workingHour.allowancePerDay || 0,
              reasonForTrip: workingHour.reasonForTrip || '',
              carRegistrationNo: workingHour.carRegistrationNo || '',
              tripStartedDate: workingHour.tripStartedDate,
              tripEndedDate: workingHour.tripEndedDate,
              totalDayOfTravels: workingHour.totalDayOfTravels || 0,
              allowanceDestination: workingHour.allowanceDestination || '',
              expenseCategory: workingHour.expenseCategory || '',
              amountInSek: workingHour.amountInSek || workingHour.amount,
              isHalfDay: typeof workingHour.isHalfDay === 'boolean' ? (workingHour.isHalfDay ? '1' : '0') : (workingHour.isHalfDay || '0'),
              foodAllowance: workingHour.foodAllowance || 0
            };

            // Build payload for timesheet API
            const payload = {
              customerId: selectedCustomerId,
              timeSheetWorkingHoursV2: [timesheetWorkingHour]
            };

            // Dispatch Redux action
            const result = await dispatch(addTimesheetWorkingHours(payload)).unwrap();

            if (result?.statusCode === 200 || result?.statusCode === 201) {
              notify.success(
                String(t('DEDUCTION_CREATED_SUCCESSFULLY')) || 'Deduction created successfully'
              );
              resolve();
            } else {
              throw new Error(result?.message || 'Failed to create deduction');
            }
          }
        } catch (error: any) {
          console.error('❌ Failed to submit deduction:', error);
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            t('DEDUCTION_SUBMIT_FAILED') ||
            'Failed to submit deduction';
          notify.error(String(errorMessage));
          reject(error);
        }
      });
    },
    [
      editingDeductionData,
      selectedCustomerId,
      preferences,
      globalData,
      userId,
      dispatch,
      t
    ]
  );

  return {
    handleDeductionSubmit
  };
};
